import { captureAuthError, renderAuthError } from '@formosa/shared/i18n/authErrorState';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { memberApi } from '@formosa/shared/api/member';
import { getInviterCode, INVITER_CODE_KEY } from '@formosa/shared/constants/auth';
import { DEFAULT_REGISTER_CONFIG } from '@formosa/shared/utils/registerConfig';
import AuthModalShell from '../../components/auth/AuthModalShell';
import AuthPhoneCountryFields from '../../components/auth/AuthPhoneCountryFields';
import GoogleSignInButton from '../../components/auth/GoogleSignInButton';
import RegisterAgreementCheckbox from '../../components/auth/RegisterAgreementCheckbox';
import { useAuth } from '../../auth/AuthContext';
import { useTranslation } from '../../i18n/LocaleContext';

const TABS = {
  EMAIL: 'email',
  MOBILE: 'mobile',
};

const PASSWORD_PATTERN = /^\S{6,12}$/;

export default function RegisterPage() {
  const navigate = useNavigate();
  const { loginSuccess } = useAuth();
  const { t } = useTranslation();

  const [tab, setTab] = useState(TABS.EMAIL);
  const [registerConfig, setRegisterConfig] = useState(DEFAULT_REGISTER_CONFIG);
  const [loading, setLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [error, setError] = useState(null);
  const errorText = renderAuthError(error, t);
  const [success, setSuccess] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const [form, setForm] = useState({
    username: '',
    password: '',
    nickname: '',
    email: '',
    mobile: '',
    countryName: '',
    countryKey: '',
    phoneAreaCode: '',
    inviterCode: '',
    verifyCode: '',
  });

  function updateField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function updatePhoneCountry(patch) {
    setForm((prev) => ({
      ...prev,
      countryKey: patch.countryKey ?? prev.countryKey,
      countryName: patch.countryEnName ?? prev.countryName,
      phoneAreaCode: patch.phoneAreaCode ?? prev.phoneAreaCode,
    }));
  }

  const availableTabs = useMemo(() => {
    const tabs = [];
    if (registerConfig.emailRegister) {
      tabs.push(TABS.EMAIL);
    }
    if (registerConfig.phoneRegister) {
      tabs.push(TABS.MOBILE);
    }
    return tabs.length > 0 ? tabs : [TABS.EMAIL];
  }, [registerConfig.emailRegister, registerConfig.phoneRegister]);

  const showRegisterTabs = availableTabs.length > 1;

  const canSubmit = useMemo(() => {
    if (!agreedToTerms) {
      return false;
    }
    if (!form.username.trim()) {
      return false;
    }
    if (!PASSWORD_PATTERN.test(form.password)) {
      return false;
    }
    if (!form.verifyCode.trim()) {
      return false;
    }
    if (tab === TABS.EMAIL) {
      return Boolean(form.email.trim());
    }
    return Boolean(form.mobile.trim() && form.countryName && form.phoneAreaCode);
  }, [agreedToTerms, form, tab]);

  useEffect(() => {
    const inviter = getInviterCode();
    if (inviter) {
      localStorage.setItem(INVITER_CODE_KEY, inviter);
      setForm((prev) => (prev.inviterCode ? prev : { ...prev, inviterCode: inviter }));
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    memberApi
      .getRegisterConfig()
      .then((config) => {
        if (!cancelled) {
          setRegisterConfig(config);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!availableTabs.includes(tab)) {
      setTab(availableTabs[0]);
    }
  }, [availableTabs, tab]);

  async function handleSendVerifyCode() {
    setError(null);
    setSuccess('');
    setSendingCode(true);

    try {
      if (tab === TABS.EMAIL) {
        if (!form.email) {
          setError({ key: 'register.emailRequired' });
          return;
        }
        await memberApi.sendVerifyCodeEmail({
          scene: 'REGISTER',
          email: form.email,
        });
      } else {
        if (!form.mobile) {
          setError({ key: 'register.mobileRequired' });
          return;
        }
        await memberApi.sendVerifyCodePhone({
          scene: 'REGISTER',
          countryEnName: form.countryName,
          phoneAreaCode: form.phoneAreaCode,
          phoneNumber: form.mobile,
        });
      }
      setSuccess(tab === TABS.EMAIL ? 'emailVerification.accepted' : 'register.codeSent');
    } catch (err) {
      setError(captureAuthError(err, 'register.sendCodeFailed'));
    } finally {
      setSendingCode(false);
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!canSubmit) {
      if (!agreedToTerms) {
        setError({ key: 'register.agreeRequired' });
      }
      return;
    }

    setError(null);
    setSuccess('');
    setLoading(true);

    try {
      const payload = {
        username: form.username,
        password: form.password,
        nickname: form.nickname,
        inviterCode: form.inviterCode,
        verifyCode: form.verifyCode,
        countryName: form.countryName,
      };

      const data =
        tab === TABS.EMAIL
          ? await memberApi.registerEmail({
              ...payload,
              email: form.email,
            })
          : await memberApi.registerMobile({
              ...payload,
              mobile: form.mobile,
            });

      loginSuccess(data);
      navigate('/', { replace: true });
    } catch (err) {
      setError(captureAuthError(err, 'register.registerFailed'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthModalShell mode="register" title={t('register.title')}>
      <p className="auth-modal__subtitle">{t('register.subtitle')}</p>

      {showRegisterTabs && (
      <div className="tabs">
        {registerConfig.emailRegister && (
          <button
            type="button"
            className={`tab ${tab === TABS.EMAIL ? 'active' : ''}`}
            onClick={() => setTab(TABS.EMAIL)}
          >
            {t('register.tabEmail')}
          </button>
        )}
        {registerConfig.phoneRegister && (
          <button
            type="button"
            className={`tab ${tab === TABS.MOBILE ? 'active' : ''}`}
            onClick={() => setTab(TABS.MOBILE)}
          >
            {t('register.tabMobile')}
          </button>
        )}
      </div>
      )}

      {error && <div className="alert alert-error">{errorText}</div>}
      {success && <div className="alert alert-success">{t(success)}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-field">
          <label htmlFor="username">{t('register.username')}</label>
          <input
            id="username"
            value={form.username}
            onChange={(e) => updateField('username', e.target.value)}
            required
          />
        </div>

        <div className="form-field">
          <label htmlFor="password">{t('register.password')}</label>
          <input
            id="password"
            type="password"
            value={form.password}
            onChange={(e) => updateField('password', e.target.value)}
            required
            minLength={6}
            maxLength={12}
          />
        </div>

        <div className="form-field">
          <label htmlFor="nickname">{t('register.nickname')}</label>
          <input
            id="nickname"
            value={form.nickname}
            onChange={(e) => updateField('nickname', e.target.value)}
          />
        </div>

        {tab === TABS.EMAIL ? (
          <div className="form-field">
            <label htmlFor="email">{t('register.email')}</label>
            <input
              id="email"
              type="email"
              value={form.email}
              onChange={(e) => updateField('email', e.target.value)}
              required
            />
          </div>
        ) : (
          <>
            <AuthPhoneCountryFields
              variant="form"
              idPrefix="register-phone"
              countryKey={form.countryKey}
              countryEnName={form.countryName}
              phoneAreaCode={form.phoneAreaCode}
              onChange={updatePhoneCountry}
              allowedEnNames={registerConfig.enNames}
              disabled={loading || sendingCode}
            />
            <div className="form-field">
              <label htmlFor="mobile">{t('register.mobile')}</label>
              <input
                id="mobile"
                value={form.mobile}
                onChange={(e) => updateField('mobile', e.target.value)}
                required
              />
            </div>
          </>
        )}

        <div className="form-field">
          <label htmlFor="inviterCode">{t('register.inviterCode')}</label>
          <input
            id="inviterCode"
            value={form.inviterCode}
            onChange={(e) => updateField('inviterCode', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="verifyCode">{t('register.verifyCode')}</label>
          <div className="form-row">
            <input
              id="verifyCode"
              value={form.verifyCode}
              onChange={(e) => updateField('verifyCode', e.target.value)}
              required
            />
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleSendVerifyCode}
              disabled={sendingCode}
              style={{ flexShrink: 0 }}
            >
              {sendingCode ? t('register.sendingCode') : t('register.sendCode')}
            </button>
          </div>
        </div>

        <RegisterAgreementCheckbox
          checked={agreedToTerms}
          onChange={setAgreedToTerms}
          disabled={loading}
        />

        <button
          type="submit"
          className="auth-btn auth-btn--primary"
          disabled={loading || !canSubmit}
        >
          {loading ? t('register.submitting') : t('register.submit')}
        </button>
      </form>

      <div className="auth-divider">
        <span>{t('register.divider')}</span>
      </div>

      <GoogleSignInButton mode="register" onError={(message) => setError(message ? { key: 'authErrors.generic' } : null)} />
    </AuthModalShell>
  );
}
